/**
 * BOM管理 - 常量定义
 * 
 * 【文件职责】
 * 定义BOM管理模块的常量映射，包括枚举值的中文标签、默认配置等。
 * 用于前端界面显示和表单选项。
 * 
 * 【对应后端】
 * - backend/app/Modules/Bom/Models/Bom.php (静态属性 $lifecycleStatusMap)
 * - backend/database/seeders/PermissionSeeder.php (权限定义)
 * 
 * 【注意事项】
 * 1. 常量名使用 UPPER_SNAKE_CASE
 * 2. 标签映射的键值必须与枚举值一致
 * 3. 中文标签参考后端 Model 的静态属性
 * 4. 权限常量参考 PermissionSeeder
 * 
 * 【更新时机】
 * - 后端新增枚举值时
 * - 中文标签调整时
 * - 权限定义变更时
 * 
 * 【验证方式】
 * 对照 Bom.php 的 $lifecycleStatusMap 等静态属性，确保：
 * - 映射关系完整
 * - 中文标签准确
 * - 无遗漏、无多余
 * 
 * @last-updated 2025-10-18
 */

import { BomLifecycleStatus, BomStatus, BomItemStatus } from './enums';

/**
 * BOM 生命周期状态标签映射
 * 
 * 对应后端：Bom::$lifecycleStatusMap
 */
export const BOM_LIFECYCLE_STATUS_LABELS: Record<BomLifecycleStatus, string> = {
  [BomLifecycleStatus.DESIGN]: '设计中',
  [BomLifecycleStatus.PENDING]: '待审核',
  [BomLifecycleStatus.RELEASED]: '已发布',
  [BomLifecycleStatus.FROZEN]: '冻结',
  [BomLifecycleStatus.OBSOLETE]: '淘汰',
};

/**
 * BOM 状态标签映射
 */
export const BOM_STATUS_LABELS: Record<BomStatus, string> = {
  [BomStatus.ACTIVE]: '启用',
  [BomStatus.INACTIVE]: '停用',
};

/**
 * BOM 明细状态标签映射
 */
export const BOM_ITEM_STATUS_LABELS: Record<BomItemStatus, string> = {
  [BomItemStatus.ACTIVE]: '启用',
  [BomItemStatus.INACTIVE]: '停用',
};

/**
 * BOM 生命周期状态选项（用于表单下拉选择）
 */
export const BOM_LIFECYCLE_STATUS_OPTIONS = [
  { value: BomLifecycleStatus.DESIGN, label: '设计中' },
  { value: BomLifecycleStatus.PENDING, label: '待审核' },
  { value: BomLifecycleStatus.RELEASED, label: '已发布' },
  { value: BomLifecycleStatus.FROZEN, label: '冻结' },
  { value: BomLifecycleStatus.OBSOLETE, label: '淘汰' },
];

/**
 * BOM 状态选项（用于表单下拉选择）
 */
export const BOM_STATUS_OPTIONS = [
  { value: BomStatus.ACTIVE, label: '启用' },
  { value: BomStatus.INACTIVE, label: '停用' },
];

/**
 * BOM 明细状态选项（用于表单下拉选择）
 */
export const BOM_ITEM_STATUS_OPTIONS = [
  { value: BomItemStatus.ACTIVE, label: '启用' },
  { value: BomItemStatus.INACTIVE, label: '停用' },
];

/**
 * BOM 生命周期状态颜色映射（用于界面展示）
 */
export const BOM_LIFECYCLE_STATUS_COLORS: Record<BomLifecycleStatus, string> = {
  [BomLifecycleStatus.DESIGN]: 'blue',
  [BomLifecycleStatus.PENDING]: 'orange',
  [BomLifecycleStatus.RELEASED]: 'green',
  [BomLifecycleStatus.FROZEN]: 'gray',
  [BomLifecycleStatus.OBSOLETE]: 'red',
};

/**
 * ⚠️ BOM 权限常量已迁移至统一权限管理
 * 
 * 请从 @bayes-mes/shared 导入标准权限常量：
 * 
 * BOM 管理权限：
 * - PERM_BOM_BOM_VIEW
 * - PERM_BOM_BOM_CREATE
 * - PERM_BOM_BOM_UPDATE
 * - PERM_BOM_BOM_DELETE
 * - PERM_BOM_BOM_APPROVE
 * - PERM_BOM_BOM_FREEZE
 * - PERM_BOM_BOM_OBSOLETE
 * 
 * 替代料管理权限：
 * - PERM_BOM_SUBSTITUTE_MATERIAL_VIEW
 * - PERM_BOM_SUBSTITUTE_MATERIAL_CREATE
 * - PERM_BOM_SUBSTITUTE_MATERIAL_UPDATE
 * - PERM_BOM_SUBSTITUTE_MATERIAL_DELETE
 * 
 * @deprecated 请使用 constants/api-permissions.ts 中的权限常量
 * @see bayes-mes-shared/constants/api-permissions.ts
 */

/**
 * BOM 默认配置
 */
export const BOM_DEFAULT_CONFIG = {
  /** 默认版本号 */
  DEFAULT_VERSION: '1.0',
  /** 默认状态 */
  DEFAULT_STATUS: BomStatus.ACTIVE,
  /** 默认生命周期状态 */
  DEFAULT_LIFECYCLE_STATUS: BomLifecycleStatus.DESIGN,
  /** 默认是否为默认版本 */
  DEFAULT_IS_DEFAULT: false,
  /** 默认行号步进 */
  DEFAULT_LINE_NUMBER_STEP: 10,
  /** 复杂BOM阈值（明细数量） */
  COMPLEX_BOM_THRESHOLD: 20,
} as const;

/**
 * BOM 明细默认配置
 */
export const BOM_ITEM_DEFAULT_CONFIG = {
  /** 默认损耗率（百分比） */
  DEFAULT_SCRAP_RATE: 0,
  /** 默认状态 */
  DEFAULT_STATUS: BomItemStatus.ACTIVE,
  /** 最小用量 */
  MIN_QUANTITY: 0.000001,
  /** 最大损耗率（百分比） */
  MAX_SCRAP_RATE: 100,
} as const;

/**
 * BOM 排序字段选项
 */
export const BOM_SORT_FIELD_OPTIONS = [
  { value: 'created_at', label: '创建时间' },
  { value: 'updated_at', label: '更新时间' },
  { value: 'bom_code', label: 'BOM编码' },
  { value: 'bom_name', label: 'BOM名称' },
  { value: 'version', label: '版本号' },
];

/**
 * 排序方向选项
 */
export const SORT_ORDER_OPTIONS = [
  { value: 'asc', label: '升序' },
  { value: 'desc', label: '降序' },
];

/**
 * BOM 验证约束常量
 *
 * 对应后端：CreateBomRequest::rules()、UpdateBomRequest::rules()
 * 用于前端表单验证，确保前后端验证规则一致
 */
export const BOM_VALIDATION_RULES = {
  /** BOM编码最大长度 */
  BOM_CODE_MAX_LENGTH: 50,
  /** BOM编码最小长度 */
  BOM_CODE_MIN_LENGTH: 2,
  /** BOM名称最大长度 */
  BOM_NAME_MAX_LENGTH: 100,
  /** BOM名称最小长度 */
  BOM_NAME_MIN_LENGTH: 2,
  /** 版本号最大长度 */
  VERSION_MAX_LENGTH: 20,
  /** 描述最大长度 */
  DESCRIPTION_MAX_LENGTH: 500,
  /** 备注最大长度 */
  REMARK_MAX_LENGTH: 500,
  /** 工厂编码最大长度 */
  FACTORY_CODE_MAX_LENGTH: 50,
} as const;

/**
 * BOM 明细验证约束常量
 * 
 * 对应后端：CreateBomItemRequest::rules()、UpdateBomItemRequest::rules()
 */
export const BOM_ITEM_VALIDATION_RULES = {
  /** 标准用量最小值 */
  QUANTITY_MIN: 0.000001,
  /** 单位最大长度 */
  UNIT_MAX_LENGTH: 20,
  /** 损耗率最小值（百分比） */
  SCRAP_RATE_MIN: 0,
  /** 损耗率最大值（百分比） */
  SCRAP_RATE_MAX: 100,
  /** 工序编码最大长度 */
  OPERATION_CODE_MAX_LENGTH: 50,
  /** 位置编码最大长度 */
  POSITION_CODE_MAX_LENGTH: 50,
  /** 位置描述最大长度 */
  POSITION_DESCRIPTION_MAX_LENGTH: 200,
  /** 备注最大长度 */
  REMARK_MAX_LENGTH: 500,
} as const;

